/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-02 17:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Notification from './notification';
import InvalidMetafieldsFound from 'src/components/views/tools/metafield/invalid-metafields-found';
import NotifPublishThemeV2 from 'src/components/views/dashboard/notifications/notif-publish-theme-v2';
import NotifProductVisibilityCustomRangeSlider from 'src/components/views/dashboard/notifications/notif-product-visibility-custom-range-slider';
import { getShop } from 'src/helpers';
import { getDisplayNotifications } from 'src/helpers/dashboard-helper';
import { getFullAppUrlV2, buildUpdateAppPermissionURLV2 } from 'src/helpers';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import RegularPlanExpiredModal from 'src/components/containers/common/notificationSubscription/regular-plan-expired-modal';

import * as notificationAction from 'actions/dashboard/notification-actions';
import * as filterDataActions from 'actions/filter-data-actions';
import * as metafieldActions from 'src/stores/actions/metafield-actions';

type Props = {
  getNotifications: () => void,
  dismissNotification: () => void,
  notifications: Array<Object>
};

type ComposedProps = Props & WithI18nProps;
class SystemNotifications extends Component<ComposedProps> {
  componentDidMount() {
    const { getNotifications, getFilterDataByType, getMetafields } = this.props;

    // Get list of notification
    getNotifications();

    getFilterDataByType('metafields');
    getMetafields();
  }

  renderNotification(notification) {
    const { type } = notification;
    const { dismissNotification } = this.props;

    const component = {
      info: 'info',
      warn: 'warning',
      error: 'critical',
      success: 'success'
    };

    return component[type] ? (
      <Notification
        status={component[type]}
        notification={notification}
        onDismiss={dismissNotification}
      />
    ) : (
      ''
    );
  }

  renderCustomNotification = () => {
    const {
      invalidMetafields,
      emptyMetafields,
      tenantApp = {},
      themesLoading,
      existedThemeLibV2,
      emailSupport
    } = this.props;
    const {
      liveThemeV2 = false,
      liveThemeV3 = false,
      adminAppFeatures = {},
      upgradeThemeV2 = {}
    } = tenantApp;
    const { themeSetupV2 = false } = adminAppFeatures;
    const { installStatus = '', upgradeUsingThemeSetup = false } = upgradeThemeV2;

    const errorMetafields = [].concat(invalidMetafields, emptyMetafields);

    const components = [];

    // Add notif for invalid metafields
    if (errorMetafields.length > 0) {
      components.push(
        <InvalidMetafieldsFound
          invalidMetafields={invalidMetafields}
          emptyMetafields={emptyMetafields}
          hasAction={true}
        />
      );
    }

    // Add notif for stores that have upgraded into V2 theme but hasn't published
    // Applied only for stores that are new to V2 (i.e switching from V1 to V2)
    if (!liveThemeV2 && themeSetupV2 && !liveThemeV3 && !themesLoading && existedThemeLibV2) {
      if (upgradeUsingThemeSetup)
        // Upgrade V2 using Theme Setup
        components.push(
          <NotifPublishThemeV2 usingThemeUpgrade={false} emailSupport={emailSupport} />
        );
      else if (installStatus === 'success')
        // Upgrade V2 using Theme Upgrade
        components.push(
          <NotifPublishThemeV2 usingThemeUpgrade={true} emailSupport={emailSupport} />
        );
    }

    return components.length > 0 ? (
      <div className="notification">
        {components.map((component, index) => {
          return <React.Fragment key={`custom-notif-${index}`}>{component}</React.Fragment>;
        })}
      </div>
    ) : (
      ''
    );
  };

  renderNewProductVisibility = () => {
    const date = Date.now();

    // Display from August 26 2020 (morning, deploy time). Takedown after the end of Sep 2nd 2020
    return date >= 1598400000000 && date <= 1599091200000 ? (
      <NotifProductVisibilityCustomRangeSlider />
    ) : (
      ''
    );
  };

  render() {
    const { notifications } = this.props;

    // Determine blocks to be rendered
    const displayNotifications = getDisplayNotifications(notifications, getShop());

    // Verify and remove applying special discount code expired soon
    let filteredNotifications = this.verifySpecialDiscountCodeExpiredSoon(displayNotifications);

    // Update notification for beta release
    filteredNotifications = this.changeActionLink(filteredNotifications);

    // Update internal link in content
    filteredNotifications = this.changeContentLink(filteredNotifications);

    return (
      <React.Fragment>
        <ExceededPlanNotification />

        {/* Notification for regular plan expire soon */}
        <RegularPlanExpiredModal />

        {this.renderNewProductVisibility()}

        {filteredNotifications.map((notification, index) => {
          return (
            <React.Fragment key={'notification-' + index}>
              {this.renderNotification(notification)}{' '}
            </React.Fragment>
          );
        })}

        {this.renderCustomNotification()}
      </React.Fragment>
    );
  }

  // Verify subscription status and remove notification if needed
  verifySpecialDiscountCodeExpiredSoon = notifications => {
    let { subscription, i18n } = this.props;

    const { status } = subscription;

    let filteredNotifications = notifications;
    if (status !== 'specialDiscountExpiredSoon') {
      // This is post process that helps to remove notification for special discount code expired
      // Because the conditional function for filtering notification is limited by tenant app info
      filteredNotifications = _.filter(filteredNotifications, notification => {
        return (
          notification.title !==
          i18n.translate('AdminHTML.Dashboard.Notifications.notifications_Expired')
        );
      });
    }

    return filteredNotifications;
  };

  changeActionLink = notifications => {
    const { tenantApp } = this.props;

    const updatedNotifications = _.map(notifications, notification => {
      const { action, title } = notification;

      if (_.startsWith(action, '/beta-features?utm_source=App&utm_medium=Nofication')) {
        notification.action = getFullAppUrlV2(action);
      }

      if (_.startsWith(action, '/merchandising?utm_source=BoostPFS&utm_medium=In-app')) {
        notification.action = getFullAppUrlV2(action);
      }

      if (_.startsWith(action, '/plan-discount?utm_source=BoostPFS&utm_medium=in-app')) {
        notification.action = getFullAppUrlV2(action);
      }

      if (_.startsWith(action, '/authorize?client_id')) {
        notification.action = buildUpdateAppPermissionURLV2(action);
      }

      if (_.startsWith(action, '/custom-sorting')) {
        notification.action = getFullAppUrlV2(action);
      }

      // Preview link for theme setup v2
      if (title === 'You can preview brand-new updates now!') {
        // TODO: Compare by id instead of text
        const previewLinkThemeSetupV2 = _.get(tenantApp, 'previewLinkThemeSetupV2', '');
        notification.action = previewLinkThemeSetupV2;
      }

      return notification;
    });

    return updatedNotifications;
  };

  changeContentLink = notifications => {
    const cloned = _.cloneDeep(notifications);
    const updatedNotifications = _.map(cloned, notification => {
      // Update link for product visibility
      this.changeLink(
        notification,
        '/product-visibility?utm_source=BoostPFS&utm_medium=in-app%20notification&utm_campaign=New%20Feature%20Release%20Aug%202020'
      );

      // Update link for custom range slider
      this.changeLink(
        notification,
        '/custom-range-slider?utm_source=BoostPFS&utm_medium=in-app%20notification&utm_campaign=New%20Feature%20Release%20Aug%202020'
      );

      // Update link for swatch settings
      this.changeLink(
        notification,
        '/swatch-settings?utm_source=BoostPFS&utm_medium=in-app%20noti&utm_campaign=Feature%20Release%20Mar%202021'
      );

      return notification;
    });

    return updatedNotifications;
  };

  changeLink(notification, link) {
    if (notification.content.includes(link)) {
      const updatedLink = getFullAppUrlV2(link);
      notification.content = notification.content.replace(link, updatedLink);
    }
  }
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    tenantApp,
    lastSyncDoneAt: _.get(tenantApp, 'syncControl.lastSyncDoneAt', 0), // seconds
    lastSyncDuration: _.get(tenantApp, 'syncControl.lastSyncDuration', 0), // seconds
    metafieldOptionValues: _.get(state.filterData, 'filterDataByType', []),
    metafields: _.get(state.metafield, 'metafields', []),
    invalidMetafields: _.get(state.metafield, 'invalidMetafields', []),
    emptyMetafields: _.get(state.metafield, 'emptyMetafields', []),
    notifications: _.get(state.notifications, 'list', []),
    subscription: _.get(state.oauth, 'subscription', {}),
    existedThemeLibV2: _.get(state.themeSetup, 'existedThemeLibV2', false),
    themesLoading: _.get(state.themeSetup, 'getThemesLoading', false),
    emailSupport: _.get(state.oauth, 'emailSupport')
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterDataByType: type =>
    dispatch(filterDataActions.getFilterDataByType(type, {}, ownProps.i18n)),
  getMetafields: () => dispatch(metafieldActions.getMetafields(ownProps.i18n)),
  getNotifications: () => dispatch(notificationAction.getNotifications(ownProps.i18n)),
  dismissNotification: notificationId =>
    dispatch(notificationAction.dismissNotification(notificationId))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SystemNotifications));
